/**
 * Renders non-reversal line items that are not listed in the
 * `LINE_ITEMS` array in util/types.js
 *
 * The line items are rendered so that the line item code is formatted to human
 * readable form and the line total is printed as price.
 *
 * If you require another kind of presentation for your line items, add them to
 * the `LINE_ITEMS` array in util/types.js and create a specific line item
 * component for them that can be used in the `BookingBreakdown` component.
 */
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import React, { memo } from 'react';
import { useIntl } from 'react-intl';

import { formatMoney } from '../../../util/currency';
import { nightsBetween } from '../../../util/dates';
import { FormattedMessage } from '../../../util/reactIntl';
import { EXTRA_LINE_ITEMS, LINE_ITEMS } from '../../../util/types';
import { itemValueStyles, lineItemStyles, totalDividerStyles } from './BookingBreakdown.styles';

const createItemLabel = (
  item,
  intl,
  quantity,
  seats,
  units,
  nights,
  guestCount,
  customLineItems,
  customLineItemTranslations,
) => {
  const customLineItem = customLineItems.find((cli) => cli.code === item.code);
  if (!customLineItem) return '';

  const title =
    customLineItemTranslations[item.code]?.title ||
    customLineItem.title[intl.locale] ||
    customLineItem.title['en'] ||
    '';

  const unitPrice = formatMoney(intl, item.unitPrice);

  if (quantity && !seats && !units && title) {
    return title
      ? `${unitPrice} x ${quantity} ${title}`
      : `${unitPrice} x ${quantity} ${item.code}`;
  } else if (!(quantity && !seats && !units) && title) {
    return title ? `${unitPrice} ${title}` : `${unitPrice} ${item.code}`;
  }
  return intl.formatMessage(
    { id: `BookingBreakdown.${item.code.replace('line-item/', '')}` },
    { unitPrice, units, seats },
  );
};

const createQuantityBreakdown = (item, intl, nights, guestCount, customLineItems) => {
  const customLineItem = customLineItems.find((cli) => cli.code === item.code);
  if (!customLineItem) return '';

  const { type } = customLineItem || {};
  let quantityBreakdown = '';
  if (type?.perNight) {
    quantityBreakdown += intl.formatMessage(
      { id: 'LineItemUnknownItemsMaybe.perNight' },
      { count: nights },
    );
  }
  if (type?.perGuest && type?.perNight) {
    quantityBreakdown += intl.formatMessage({
      id: 'LineItemUnknownItemsMaybe.and',
    });
  }
  if (type?.perGuest) {
    quantityBreakdown += intl.formatMessage(
      { id: 'LineItemUnknownItemsMaybe.perGuest' },
      { count: guestCount },
    );
  }
  return quantityBreakdown;
};

/**
 * @param {object} props - component props
 * @param {object} props.transaction - transaction object
 * @param {boolean} props.isProvider - is current user a provider
 * @param {object} props.customLineItemTranslations - custom line item translations
 */

const LineItemUnknownItemsMaybe = ({ transaction, isProvider, customLineItemTranslations }) => {
  const intl = useIntl();

  if (!transaction) return null;

  let items = isProvider
    ? transaction.attributes.lineItems.filter((item) => item.includeFor.includes('provider'))
    : transaction.attributes.lineItems.filter((item) => item.includeFor.includes('customer'));

  // remove base unit Item
  items = items.filter((item) => ![...LINE_ITEMS, ...EXTRA_LINE_ITEMS].includes(item.code));

  // remove refund line items
  items = items.filter((item) => item.lineTotal.amount > 0);

  const { start, end } = transaction.booking.attributes;
  const nights = nightsBetween(start, end);
  // guestCount may be missing entirely: legacy bookings only carry it as a plain
  // number, and metadata restored from a transaction (rather than fresh booking
  // data) doesn't always include it. Same tolerant pattern as CheckoutPage and
  // LineItemUnknownRefundMaybe — Object.entries(undefined) would otherwise throw.
  const { guestCount: rawGuestCount } = transaction.attributes.metadata;
  const guestCount =
    typeof rawGuestCount === 'number'
      ? rawGuestCount
      : rawGuestCount
        ? Object.entries(rawGuestCount).reduce((acc, [key, value]) => {
            if (typeof value === 'number') {
              return acc + value;
            }
            return acc;
          }, 0)
        : null;
  const customLineItems = transaction.attributes.metadata.customLineItems || [];

  if (items.length === 0) return null;

  const renderExtraLineItems = (item, index) => {
    const quantity = item.quantity;
    const seats = item.seats;
    const units = item.units;

    const label = createItemLabel(
      item,
      intl,
      quantity,
      seats,
      units,
      nights,
      guestCount,
      customLineItems,
      customLineItemTranslations,
    );

    const quantityBreakdown = createQuantityBreakdown(
      item,
      intl,
      nights,
      guestCount,
      customLineItems,
    );

    const formattedTotal = formatMoney(intl, item.lineTotal);

    return (
      <Box key={`${index}-item.code`} sx={lineItemStyles}>
        <Typography sx={{ flex: 1 }} variant="lineItemLabel">
          {label}{quantityBreakdown ? ` x ${quantityBreakdown}` : ''}
        </Typography>
        <Typography sx={itemValueStyles} variant="lineItemLabel">{formattedTotal}</Typography>
      </Box>
    );
  };

  return (
    <>
      <Divider sx={totalDividerStyles} />
      <Typography fontWeight="bold" variant="lineItemLabel">
        <FormattedMessage id="BookingBreakdown.additionalOffer" />
      </Typography>
      {items.map((item, i) => renderExtraLineItems(item, i))}
    </>
  );
};

export default memo(LineItemUnknownItemsMaybe);
